import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { completePaypal } from "@/lib/api";

export default function PayPalCheckout({ product, onPaid }) {
  const navigate = useNavigate();
  const [{ isPending, isRejected, options }] = usePayPalScriptReducer();
  const reRenderKey = useMemo(() => [product.id, product.price], [product.id, product.price]);

  if (!options["client-id"]) {
    return (
      <p className="text-soft text-xs font-mono-ui text-center py-3" data-testid="paypal-unconfigured">
        Payments are being configured. Please check back shortly.
      </p>
    );
  }

  if (isRejected) {
    return (
      <p className="text-[#d6453a] text-xs font-mono-ui text-center py-3">
        Could not load PayPal. Please refresh and try again.
      </p>
    );
  }

  return (
    <div data-testid="paypal-checkout">
      {isPending && (
        <div className="flex justify-center py-3">
          <Loader2 className="w-5 h-5 text-primary-gold animate-spin" />
        </div>
      )}
      <PayPalButtons
        style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 48 }}
        forceReRender={reRenderKey}
        createOrder={(data, actions) =>
          actions.order.create({
            intent: "CAPTURE",
            purchase_units: [
              {
                description: product.fullTitle || product.title,
                amount: { value: Number(product.price).toFixed(2) },
              },
            ],
          })
        }
        onApprove={async (data, actions) => {
          try {
            await actions.order.capture();
            if (onPaid) return onPaid(data.orderID);
            const res = await completePaypal(data.orderID, product.id);
            navigate(`/success?token=${res.download_token}`);
          } catch (e) {
            toast.error("We couldn't confirm your payment. Please contact support.");
          }
        }}
        onError={() => {
          toast.error("PayPal payment failed. Please try again.");
        }}
      />
    </div>
  );
}
